'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/classnames'

const Tabs = TabsPrimitive.Root

const TabsListVariants = cva('inline-flex items-center justify-start h-10', {
  variants: {
    variant: {
      default: 'rounded-lg bg-muted p-1 border-none',
      underline: 'w-full justify-start rounded-none border-b bg-transparent p-0',
    },
    size: {
      default: 'h-9',
      sm: 'h-8  text-xs',
      lg: 'h-10 ',
      icon: 'h-9 w-9',
    },
    width: {
      default: 'w-full',
      fit: 'w-fit',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    width: 'default',
  },
})

const TabsTriggerVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-normal  transition-all disabled:pointer-events-none data-[state=active]:text-foreground px-3',
  {
    variants: {
      variant: {
        default:
          'data-[state=active]:bg-background ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 data-[state=active]:shadow-sm disabled:opacity-50 rounded-md py-1',
        underline:
          'relative rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none focus-visible:ring-0 data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none',
      },
      size: {
        default: '',
        sm: ' text-xs',
        lg: '',
        icon: 'h-9 w-9',
      },
      width: {
        default: 'w-full',
        fit: 'w-fit',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      width: 'default',
    },
  },
)

export interface TabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof TabsListVariants> {
  asChild?: boolean
}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, variant, size, width, ...props }, ref) => (
    <TabsPrimitive.List ref={ref} className={cn(TabsListVariants({ variant, size, width, className }))} {...props} />
  ),
)
TabsList.displayName = TabsPrimitive.List.displayName

export interface TabsTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>,
    VariantProps<typeof TabsListVariants> {
  asChild?: boolean
}

const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(
  ({ className, variant, size, width, ...props }, ref) => (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(TabsTriggerVariants({ variant, size, width, className }))}
      {...props}
    />
  ),
)
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
